import React from 'react';
import * as MD from '@material-ui/core';

import { IApplicationState } from '../../Store';
import { Dispatch } from 'redux';
import { IDashboardState } from '../../Store/Dashboard/Types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Row, useTable } from 'react-table';
import { decimalToRgb, RGB_Log_Shade } from '../../Utils/Various';
import { useSticky } from "react-table-sticky";

import { CSSProperties } from 'react';
import { Theme } from '@material-ui/core/styles';

const Styles = styled.div<{ theme: Theme }>`
  padding: 1rem;

  .table-wrapper {
    max-height: 600px;
    overflow: auto;
    border: 1px solid ${({ theme }) => theme.palette.divider};
    background: ${({ theme }) => theme.palette.background.default};
    scroll-snap-type: x mandatory;
    scroll-padding-left: ${({ theme }) =>
      (theme as any).CatalogManager.dimensions.firstColumnTable}px;
  }

  table {
    border-spacing: 0;
    width: auto;
    table-layout: auto;
    background: ${({ theme }) => theme.palette.background.default};

    thead {
      position: sticky;
      z-index: 2;
      top: 0;
      box-shadow: 0px 1px 1px ${({ theme }) => theme.palette.action.hover};
      background: ${({ theme }) => theme.palette.background.paper};

      th:first-child {
        position: sticky;
        left: 0;
        z-index: 3;
        background: ${({ theme }) => theme.palette.background.paper};
        border-right: 2px solid ${({ theme }) => theme.palette.divider};
        scroll-snap-align: none;
      }

      th:not(:first-child) {
        scroll-snap-align: start;
      }
    }

    tbody {
      td:first-child {
        position: sticky;
        left: 0;
        z-index: 1;
        background: ${({ theme }) => theme.palette.background.paper};
        border-right: 2px solid ${({ theme }) => theme.palette.divider};
        scroll-snap-align: none;
      }

      td:not(:first-child) {
        scroll-snap-align: start;
      }
    }

    tfoot {
      position: sticky;
      bottom: 0;
      z-index: 2;
      box-shadow: 0px -1px 1px ${({ theme }) => theme.palette.action.hover};
      background: ${({ theme }) => theme.palette.background.paper};

      td:first-child {
        position: sticky;
        left: 0;
        z-index: 3;
        background: ${({ theme }) => theme.palette.background.paper};
        border-right: 2px solid ${({ theme }) => theme.palette.divider};
        scroll-snap-align: none;
      }

      td:not(:first-child) {
        scroll-snap-align: start;
      }
    }

    tr:last-child td {
      border-bottom: 0;
    }

    th,
    td {
      margin: 0;
      padding: 0.5rem;
      border-bottom: 1px solid #000;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      &:last-child {
        border-right: 0;
      }
    }
  }
`;

interface ICellStyle extends CSSProperties {
  cursor?: string;
  backgroundColor?: string;
  color?: string;
  padding?: string;
  fontWeight?: number;
  display?: string;
  textAlign?: 'left' | 'right' | 'center' | 'justify' | 'initial' | 'inherit';
  minWidth?: number;
  width?: number;
  maxWidth?: number;
  fontSize?: number;
}


function hiddenColumns(columns:any) {
  let cols:any = []
  columns.map((column:any) => {
    column.columns.map((c:any) => {
      if (c.show === false) {
        cols.push(c.id)
      }
      return cols
    })
    return cols
  })
  return cols
}

const getBaseCellStyle = (resizeTable: boolean): ICellStyle => ({
  padding: '2px',
  fontWeight: 500,
  display: 'table-cell',
  textAlign: 'center' as const,
  minWidth: resizeTable ? 90 : 168,
  width: resizeTable ? 90 : 168,
  maxWidth: resizeTable ? 150 : 400,
  fontSize: 10,
  cursor: 'pointer',
});

const getBackgroundColor = (cell: any, dashboard: IDashboardState, index: number) => {
  const columnId = cell.column.parent?.cid || cell.column.cid;
  // Skip columns without political_regime_id (like Libelle column)
  if (columnId === undefined) {
    return 'white';
  }
  const selectedColumn = dashboard.politicalRegimeList.find((x) => x.political_regime_id === columnId);
  if (!selectedColumn) {
    console.warn(`No column found for political_regime_id: ${columnId}`);
    return 'white';
  }
  const regimeSpecificColor = getRegimeColor(selectedColumn.political_regime_name);
  if (regimeSpecificColor) {
    // Check if this regime has specific alternating colors for sub-columns
    const alternatingColors = getRegimeAlternatingColors(selectedColumn.political_regime_name);
    if (alternatingColors && typeof cell.column.id === 'number') {
      return index % 2 === 0 ? alternatingColors.color1 : alternatingColors.color2;
    }
    return typeof cell.column.id === 'number'
      ? (index % 2 === 0 ? RGB_Log_Shade(0.3, regimeSpecificColor) : RGB_Log_Shade(0.6, regimeSpecificColor))
      : regimeSpecificColor;
  }
  const { red, green, blue } = decimalToRgb(selectedColumn.color);
  const baseColor = `rgb(${red}, ${green}, ${blue})`;
  return typeof cell.column.id === 'number'
    ? (index % 2 === 0 ? RGB_Log_Shade(0.3, baseColor) : RGB_Log_Shade(0.6, baseColor))
    : baseColor;
};


const getTextColor = (backgroundColor: string, cell: any) => {
  if (backgroundColor === 'white') return 'black';
  if (typeof cell.column.id === 'number' && cell.column.id === cell.column.parent?.id) return 'white';
  if (typeof cell.column.id === 'string' && cell.column.id.includes('Total')) return 'black';
  return 'black';
};

const tdStyle = (dashboard: IDashboardState, cell: any, index: number, resizeTable: boolean, theme: any): ICellStyle => {
  const baseStyle = getBaseCellStyle(resizeTable);
  const backgroundColor = getBackgroundColor(cell, dashboard, index);
  const color = getTextColor(backgroundColor, cell);

  // Applique toujours la couleur de fond et de texte calculées
  let style: ICellStyle = { ...baseStyle, backgroundColor, color };

  // Cas spécifiques
  if (cell.value === '--') {
    style = { ...style, cursor: 'default' };
  } else if (typeof cell.column.id === 'string' && cell.column.id.includes('Cumuls')) {
    style = { ...style, padding: '0px', color: backgroundColor === 'white' ? 'black' : 'white' };
  } else if (typeof cell.column.id === 'string' && cell.column.id.includes('Libelle')) {
    const firstColWidth = theme?.CatalogManager?.dimensions?.firstColumnTable || 190;
    style = { ...style, color: 'black', textAlign: 'left', minWidth: firstColWidth, width: firstColWidth, maxWidth: firstColWidth };
  }

  return style;
};


function getRegimeColor(regimeName: string): string | null {
    const colorMap: { [key: string]: string } = {
      'PRES REP HORS DEBAT': '#61325d',
      'EXECUTIF': '#ca3a7d',
      'PARTIS ET GROUP POL': '#333455'
    };
    return colorMap[regimeName] || null;
}

function getRegimeAlternatingColors(regimeName: string): { color1: string; color2: string } | null {
    const alternatingColorMap: { [key: string]: { color1: string; color2: string } } = {
      'EXECUTIF': { color1: '#db94ae', color2: '#ebc9d5' },
      'PARTIS ET GROUP POL': { color1: '#c8c8cd', color2: '#92929d' }
    };
    return alternatingColorMap[regimeName] || null;
}

const getBaseHeaderStyle = (resizeTable: boolean): ICellStyle => ({
  fontWeight: 500,
  display: 'table-cell',
  wordBreak: 'break-word' as const,
  width: resizeTable ? 90 : 168,
  minWidth: resizeTable ? 90 : 168,
  maxWidth: resizeTable ? 150 : 400,
  fontSize: 10,
});

const getHeaderBackgroundColor = (column: any, list: any[], index: number) => {
  const columnId = column.parent?.cid || column.cid;
  // Skip columns without political_regime_id (like Libelle column)
  if (columnId === undefined) {
    return { backgroundColor: 'white', color: 'black' };
  }
  const selectedColumn = list.find((x) => x.political_regime_id === columnId);
  if (!selectedColumn) {
    console.warn(`No column found for political_regime_id: ${columnId}`);
    return { backgroundColor: 'white', color: 'black' };
  }
  const regimeSpecificColor = getRegimeColor(selectedColumn.political_regime_name);
  if (regimeSpecificColor) {
    // Check if this regime has specific alternating colors for sub-columns
    const alternatingColors = getRegimeAlternatingColors(selectedColumn.political_regime_name);
    if (alternatingColors && column.parent?.columns?.length > 1 && typeof column.id === 'number') {
      const backgroundColor = index % 2 === 0 ? alternatingColors.color1 : alternatingColors.color2;
      return { backgroundColor, color: 'black' };
    }
    const backgroundColor = column.parent?.columns?.length > 1 && typeof column.id === 'number'
      ? (index % 2 === 0 ? RGB_Log_Shade(0.3, regimeSpecificColor) : RGB_Log_Shade(0.6, regimeSpecificColor))
      : regimeSpecificColor;
    return {
      backgroundColor,
      color: column.parent?.columns?.length > 1 && typeof column.id === 'number' ? 'black' : 'white',
    };
  }
  const { red, green, blue } = decimalToRgb(selectedColumn.color);
  const baseColor = `rgb(${red}, ${green}, ${blue})`;
  const backgroundColor = column.parent?.columns?.length > 1 && typeof column.id === 'number'
    ? (index % 2 === 0 ? RGB_Log_Shade(0.3, baseColor) : RGB_Log_Shade(0.6, baseColor))
    : baseColor;
  return { backgroundColor, color: column.parent?.columns?.length > 1 ? 'black' : 'white' };
};

const setThStyle = (list: any[], column: any, index: number, resizeTable: boolean): ICellStyle => {
  const baseStyle = getBaseHeaderStyle(resizeTable);
  const { backgroundColor, color } = getHeaderBackgroundColor(column, list, index);
  return { ...baseStyle, backgroundColor, color };
};

function isPercentLabel(label: any): boolean {
  return typeof label === "string" && label.indexOf("%") > -1;
}

function prepareHeaders(row: any, props: IOwnProps) {
  if (props.dashboard.filters.date_type === "month") {
    if (
      typeof row.original.Libelle === "string" &&
      row.original.Libelle.indexOf("Total") < 0
    ) {
      return row;
    }
    return null;
  }
  return row;
}

function prepareFooters(row: any, props: IOwnProps) {
  if (
    typeof row.original.Libelle === "string" &&
    row.original.Libelle.indexOf("Total") > -1
  ) {
    return row;
  }
  return null;
}

function Table(props:IOwnProps) {
  let columns:any = props.columns
  let data:any = props.data
  const hidePercentRows =
    props.dashboard.filters.display_format === "duration";
  const filteredData = React.useMemo(
    () =>
      hidePercentRows
        ? data.filter(
            (row: any) => !isPercentLabel(row.Libelle) || (typeof row.Libelle === 'string' && row.Libelle.includes('Total'))
          )
        : data,
    [data, hidePercentRows]
  );
  const {
    getTableProps, 
    getTableBodyProps, 
    headerGroups, 
    rows, 
    prepareRow,
  } = 
  useTable({
    columns, 
    data: filteredData,
    //defaultColumn,
    initialState: {
      hiddenColumns: hiddenColumns(columns)
    },
  },
  useSticky
  )

  // Render Data Table UI
  return (
    <div className="table-wrapper">
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map(headerGroup => {
            return (
              <tr {...headerGroup.getHeaderGroupProps()} >
                {headerGroup
                  .headers
                  .map((column:any, index:number) => {
                    let any = setThStyle(props.dashboard.politicalRegimeList, column, index, props.resizeTable)
                    return (
                    <th {...column.getHeaderProps()}
                    style={any}
                    >
                      {column.render('Header')}
                    </th>
                  )})
                }
              </tr>
            )}
          )}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((r:Row, i) => {
            let row:Row | null = prepareHeaders(r, props)
            if (row !== null) {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}
                >
                  {row
                    .cells
                    .map((cell, index) => {
                      return <td {...cell.getCellProps()} 
                      onClick={() => props.rowClick(null, row, cell.column) }
                      style={tdStyle(props.dashboard, cell, index, props.resizeTable, props.theme)}
                      >{cell.render('Cell')}</td>
                    })}
                </tr>
              )
            } else return null
          })}
        </tbody>
        { props.dashboard.filters.date_type === 'month' && 
        <tfoot>
          {rows.map((r:Row, i) => {  
            let row:Row | null = prepareFooters(r, props)
            if (row !== null) {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}
                style={{
                  borderTop: '1px solid black'
                }}
                >
                  {row
                    .cells
                    .map((cell, index) => {
                      return <td {...cell.getCellProps()} 
                      onClick={() => props.rowClick(null, row, cell.column) }
                      style={{
                        ...tdStyle(props.dashboard, cell, index, props.resizeTable, props.theme),
                        borderTop: '1px solid black',
                        borderBottom: '0px solid black'
                      }}
                      >{cell.render('Cell')}</td>
                    })}
                </tr>
              )
            } else return null
          })}
        </tfoot>
        }
      </table>
    </div>
  )
}

interface IPropsFromState {
  dashboard: IDashboardState
}
  
interface IPropsFromDispatch {
}
  
interface IOwnProps {
    resizeTable: boolean,
    data: any[],
    columns:any[],
    rowClick: (state: any, rowInfo: any | undefined, column: any) => void,
    dashboard?:any,
    theme: any
}

type AllProps =
    MD.WithTheme &
    IPropsFromState &
    IPropsFromDispatch &
    IOwnProps;

interface IOwnState {
  columns: any[],
}

export class TableDashboardComponent extends React.Component<AllProps, IOwnState> {
    
    public constructor(props: AllProps) {
        super(props);
        this.state = {
          columns: [],
        }
    }

    componentDidMount() {

    }
    componentDidUpdate(prevProps: AllProps) {
    
    }
    
    getTrProps = (state:any, rowInfo:any, instance:any) => {
      if (rowInfo) {
        return {
          style: {
            height: '25px'
          }
        }
      }
      return {};
    }
    render() {
  return (
    <Styles theme={this.props.theme}>
      <Table
        resizeTable={this.props.resizeTable}
        data={this.props.data}
        columns={this.props.columns}
        rowClick={this.props.rowClick.bind(this)}
        dashboard={this.props.dashboard}
        theme={this.props.theme}
      />
    </Styles>
  );
}
}

const mapStateToProps = ({ i18n, dashboard }: IApplicationState) => ({
  i18n: i18n.i18n,
  dashboard
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
});

export const TableDashboard = connect(mapStateToProps, mapDispatchToProps)(
  MD.withTheme(TableDashboardComponent)
);